/**
 * ReplacePortfolioModal — Round P108z (Feb 2026).
 *
 * Rendered when the user hits their 5/5 portfolio cap while trying
 * to Undo a Delete. Shows the name of the portfolio they're about
 * to restore, plus a dropdown of every current portfolio so they
 * can pick one to delete (cascading) to free up a slot.
 *
 * Two buttons:
 *   • Complete Transaction — POSTs undo with replace_portfolio_id.
 *   • Cancel — closes the modal. The persistent undo pill in the
 *     PortfolioSelector stays visible for the remaining TTL so the
 *     user can try again once they've manually cleaned up.
 */
import { useEffect, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReplacePortfolioModal({
  open,
  onClose,
  snapshotId,
  restoreName,
  portfolios,
  currentCount,
  maxPortfolios,
  onRestored,
}) {
  const [selectedPid, setSelectedPid] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedPid('');
      setSubmitting(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const selectedPortfolio = portfolios.find((p) => p.portfolio_id === selectedPid);

  const handleComplete = async () => {
    if (!selectedPid) {
      toast.error('Pick a portfolio to replace first.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/portfolio/undo/${snapshotId}`,
        { replace_portfolio_id: selectedPid },
        { withCredentials: true },
      );
      toast.success(
        `Restored "${data?.portfolio_name || restoreName}"`,
        {
          description: `${data?.positions_restored || 0} position(s) and ${data?.transactions_restored || 0} transaction(s) put back. Removed "${selectedPortfolio?.name || 'selected portfolio'}" to free the slot.`,
        },
      );
      try { window.dispatchEvent(new Event('portfolio-changed')); } catch { /* SSR */ }
      try { window.dispatchEvent(new Event('cash-changed')); } catch { /* SSR */ }
      onRestored?.(data);
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : detail?.message;
      toast.error('Restore failed', {
        description: msg || 'Try again or cancel to keep things as-is.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[75] flex items-center justify-center bg-black/60 backdrop-blur-sm p-3 sm:p-4"
      onClick={onClose}
      data-testid="replace-portfolio-modal-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md max-h-[85vh] bg-card border rounded-xl shadow-2xl overflow-hidden flex flex-col"
        data-testid="replace-portfolio-modal"
      >
        <div className="flex items-start justify-between gap-2 px-4 py-3 sm:px-5 border-b bg-amber-50/60 dark:bg-amber-950/30">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 shrink-0 text-amber-600 dark:text-amber-400 mt-0.5" />
            <div>
              <h3 className="text-sm sm:text-base font-bold" data-testid="replace-portfolio-title">
                Portfolio cap reached ({currentCount}/{maxPortfolios})
              </h3>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                Pick a portfolio to remove so <span className="font-semibold text-foreground">&ldquo;{restoreName}&rdquo;</span> can be restored.
              </p>
            </div>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="shrink-0 w-7 h-7 flex items-center justify-center rounded-md text-foreground/50 hover:text-foreground hover:bg-muted/60 transition-colors"
            data-testid="replace-portfolio-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-y-auto p-4 sm:p-5 space-y-3">
          <label className="block">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground block mb-1">
              Portfolio to remove <span className="text-rose-600 dark:text-rose-400">*</span>
            </span>
            <select
              value={selectedPid}
              onChange={(e) => setSelectedPid(e.target.value)}
              className="w-full h-10 rounded-lg border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="replace-portfolio-select"
            >
              <option value="" disabled>— Pick one to delete —</option>
              {portfolios.map((p) => (
                <option key={p.portfolio_id} value={p.portfolio_id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>

          {selectedPortfolio && (
            <div
              className="rounded-md border border-rose-300 dark:border-rose-700/60 bg-rose-50/50 dark:bg-rose-950/20 px-3 py-2 text-[12px] leading-relaxed"
              data-testid="replace-portfolio-warning"
            >
              <p className="font-semibold text-rose-800 dark:text-rose-200 mb-0.5">
                &ldquo;{selectedPortfolio.name}&rdquo; will be permanently deleted.
              </p>
              <p className="text-rose-700/90 dark:text-rose-300/90 text-[11px]">
                Every position and cash transaction inside it will be dropped. This action cannot be undone (only the incoming restore is protected).
              </p>
            </div>
          )}

          <p className="text-[11px] text-muted-foreground leading-relaxed">
            Cancel keeps your current setup untouched. Your Undo pill stays available for the remaining TTL so you can try again after manually cleaning up.
          </p>
        </div>

        <div className="flex items-center justify-end gap-2 px-4 sm:px-5 py-3 border-t bg-muted/20">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="h-9 px-4 rounded-md border text-sm font-medium hover:bg-muted/50 transition-colors disabled:opacity-60"
            data-testid="replace-portfolio-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleComplete}
            disabled={!selectedPid || submitting}
            className="h-9 px-4 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="replace-portfolio-complete"
          >
            {submitting ? 'Restoring…' : 'Complete Transaction'}
          </button>
        </div>
      </div>
    </div>
  );
}
